import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { renderInline } from "../lib/content";
import { useSeo } from "../lib/useSeo";
import { ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";

// A line that contains ONLY an image: ![alt](url), rendered as its own block.
const IMAGE_ONLY_RE = /^!\[([^\]]*)\]\((\S+?)\)$/;

function renderBody(text) {
  const lines = (text || "").split("\n");
  const blocks = [];
  let buf = [];
  let listBuf = [];

  const flushPara = () => {
    if (buf.length) {
      blocks.push({ type: "p", content: buf.join(" ").trim() });
      buf = [];
    }
  };
  const flushList = () => {
    if (listBuf.length) {
      blocks.push({ type: "ul", items: [...listBuf] });
      listBuf = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    const imgMatch = line.match(IMAGE_ONLY_RE);
    if (imgMatch) {
      flushPara(); flushList();
      blocks.push({ type: "img", alt: imgMatch[1], src: imgMatch[2] });
    } else if (line.startsWith("## ")) {
      flushPara(); flushList();
      blocks.push({ type: "h2", content: line.replace(/^##\s+/, "") });
    } else if (line.startsWith("### ")) {
      flushPara(); flushList();
      blocks.push({ type: "h3", content: line.replace(/^###\s+/, "") });
    } else if (line.startsWith("- ")) {
      flushPara();
      listBuf.push(line.slice(2));
    } else if (/^\d+\.\s/.test(line)) {
      flushPara();
      listBuf.push(line.replace(/^\d+\.\s+/, ""));
    } else if (line === "") {
      flushPara(); flushList();
    } else {
      flushList();
      buf.push(line);
    }
  }
  flushPara(); flushList();

  return blocks.map((b, i) => {
    if (b.type === "h2") return <h2 key={i}>{renderInline(b.content)}</h2>;
    if (b.type === "h3") return <h3 key={i}>{renderInline(b.content)}</h3>;
    if (b.type === "ul") return (
      <ul key={i}>
        {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
      </ul>
    );
    if (b.type === "img") return (
      <img key={i} src={b.src} alt={b.alt} className="rounded-2xl my-6 w-full h-auto" data-testid="insight-body-image" />
    );
    return <p key={i}>{renderInline(b.content)}</p>;
  });
}

export default function InsightDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [related, setRelated] = useState([]);

  useSeo({
    title: post?.title || "Article - NBR Group Blogs & Guidance",
    description: post?.meta_description || post?.excerpt || "Plain-English UK business name protection guidance from NBR Group.",
  });

  useEffect(() => {
    setLoading(true);
    setError("");
    api
      .get(`/insights/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError("This article could not be found."))
      .finally(() => setLoading(false));

    api.get("/insights").then((r) => {
      const all = r.data || [];
      setRelated(all.filter((p) => p.slug !== slug).slice(0, 3));
    });
  }, [slug]);

  if (loading) {
    return (
      <main className="py-20 text-center text-[#447694]" data-testid="insight-loading">
        <h1 className="sr-only">Loading article - NBR Group Blogs & Guidance</h1>
        Loading
      </main>
    );
  }
  if (error || !post) {
    return (
      <main className="py-20 text-center" data-testid="insight-not-found">
        <h1 className="font-display text-[clamp(1.75rem,3.5vw,3rem)] text-[#04273d] font-bold tracking-tight">Article not found.</h1>
        <p className="text-[#447694] mt-3">The article you&rsquo;re looking for may have been moved or removed.</p>
        <Link to="/insights" className="btn-secondary mt-6 inline-flex">Back to Blogs & Guidance</Link>
      </main>
    );
  }

  return (
    <main data-testid="insight-detail-page">
      <article className="pt-12 pb-12">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <Link to="/insights" className="inline-flex items-center gap-1 text-sm text-[#447694] hover:text-[#04273d]" data-testid="insight-back-link">
            <ArrowLeft size={14} /> Back to Blogs & Guidance
          </Link>
          <div className="mt-6">
            <Link
              to={`/insights?focus=${(post.category || "General").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
              className="bnp-chip bnp-chip-teal hover:brightness-95 transition"
              data-testid="insight-category-chip"
            >
              {post.category}
            </Link>
          </div>
          <h1 className="font-display text-[clamp(1.75rem,3.5vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight leading-[1.1]" data-testid="insight-title">{post.title}</h1>
          <p className="text-xs text-[#447694] mt-4">
            {new Date(post.created_at || Date.now()).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} · {post.read_minutes || 5} min read
          </p>

          {post.cover_image && (
            <div className="mt-8 rounded-3xl overflow-hidden">
              <img src={post.cover_image} alt="" className="w-full h-72 md:h-96 object-cover" />
            </div>
          )}
        </div>

        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 mt-10 bnp-prose" data-testid="insight-body">
          {renderBody(post.body || "")}
        </div>

        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 mt-12">
          <div className="bnp-card p-7 bg-gradient-to-br from-[#04273d] to-[#0a3b59] text-white border-0" data-testid="insight-cta-card">
            <ShieldCheck size={26} className="text-[#71edc9]" />
            <h3 className="font-display text-xl mt-3 font-semibold">See what your name&rsquo;s risk score is.</h3>
            <p className="text-sm text-[#9ac7f7] mt-2 leading-relaxed">
              30-second check. Free. No card needed.
            </p>
            <Link to="/check" className="btn-primary mt-5 inline-flex">
              Run a free check <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="py-12 border-t border-[#9ac7f7]/25" data-testid="insight-related-section">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
            <h2 className="font-display text-2xl text-[#04273d] font-semibold mb-6">More from Blogs & Guidance</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {related.map((p) => (
                <Link key={p.id} to={`/insights/${p.slug}`} className="bnp-card p-5 hover:-translate-y-1 transition-transform">
                  <span className="text-xs text-[#447694] tracking-widest uppercase font-semibold">{p.category}</span>
                  <h3 className="font-display text-base text-[#04273d] mt-1.5 font-semibold leading-snug">{p.title}</h3>
                  <p className="text-sm text-[#447694] mt-2 line-clamp-2">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
